import { c as _c } from "react/compiler-runtime";
import React, { useCallback } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { SearchableSelect, SearchableSelectProps } from 'record-it-components/input/SearchableSelect/SearchableSelect';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { allMonths, Month } from './Month';
import styles from './MonthSelect.module.css';
export type MonthSelectProps<T extends MonthSelectOption> = Omit<SearchableSelectProps<T>, 'label' | 'items' | 'getDisplayTextOfItem' | 'getKeyOfItem' | 'value' | 'onValueChanged' | 'className'> & {
  /**
   * Month selected by default.
   */
  readonly month: T;
  /**
   * Months the user can select from.
   */
  readonly availableMonths: Array<T>;
  /**
   * Called whenever the user selects a new month.
   */
  readonly onMonthSelected: (selectedMonth: T) => void;
};
export const MonthSelect = React.memo(props => {
  const $ = _c(18);
  let t0;
  if ($[0] !== props) {
    t0 = prepareProps({
      props,
      ignoreChildren: true
    });
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let availableMonths;
  let month;
  let onMonthSelected;
  let remainingProps;
  if ($[2] !== t0) {
    ({
      month,
      availableMonths,
      onMonthSelected,
      ...remainingProps
    } = t0);
    $[2] = t0;
    $[3] = availableMonths;
    $[4] = month;
    $[5] = onMonthSelected;
    $[6] = remainingProps;
  } else {
    availableMonths = $[3];
    month = $[4];
    onMonthSelected = $[5];
    remainingProps = $[6];
  }
  const {
    t
  } = useTranslation();
  let t1;
  if ($[7] !== t) {
    t1 = mon => t(monthToTk[mon]).toString();
    $[7] = t;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const getDisplayTextOfMonth = t1;
  const getKeyOfMonth = _temp;
  let t2;
  if ($[9] !== t) {
    t2 = t("components.input.MonthSelect.label");
    $[9] = t;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== availableMonths || $[12] !== getDisplayTextOfMonth || $[13] !== month || $[14] !== onMonthSelected || $[15] !== remainingProps || $[16] !== t2) {
    t3 = <SearchableSelect {...remainingProps} label={t2} items={availableMonths} getDisplayTextOfItem={getDisplayTextOfMonth} getKeyOfItem={getKeyOfMonth} value={month} onValueChanged={onMonthSelected} className={styles.monthSelect} data-testid="MonthSelect" />;
    $[11] = availableMonths;
    $[12] = getDisplayTextOfMonth;
    $[13] = month;
    $[14] = onMonthSelected;
    $[15] = remainingProps;
    $[16] = t2;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  return t3;
}) as <T extends MonthSelectOption = Month>(props: MonthSelectProps<T>) => React.JSX.Element;

/**
 * Represents "All Months in a given year".
 * Useful if you want to use the MonthSelect paired with a ExportYearSelect,
 * and would like that the user could either select a specific month in a year or the whole year.
 */
export const allMonthsOption = Symbol('allMonths');
export type MonthSelectOption = Month | typeof allMonthsOption;

/**
 * An array of all months in a year, including the "All" option.
 */
export const allMonthsInYearIncludingAny: Array<MonthSelectOption> = [...allMonths, allMonthsOption];

/**
 * Mapping from a month to its translation string.
 */
export const monthToTk: Record<MonthSelectOption, Parameters<TFunction<'time', undefined>>[0]> = {
  [allMonthsOption]: 'date.monthLong.allMonths',
  [Month.January]: 'date.monthLong.january',
  [Month.February]: 'date.monthLong.february',
  [Month.March]: 'date.monthLong.march',
  [Month.April]: 'date.monthLong.april',
  [Month.May]: 'date.monthLong.may',
  [Month.June]: 'date.monthLong.juni',
  [Month.July]: 'date.monthLong.july',
  [Month.August]: 'date.monthLong.august',
  [Month.September]: 'date.monthLong.september',
  [Month.October]: 'date.monthLong.october',
  [Month.November]: 'date.monthLong.november',
  [Month.December]: 'date.monthLong.december'
};
function _temp(mon_0) {
  return String(mon_0);
}